import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import _ from 'underscore';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { Tooltip } from 'react-tooltip';

import services from 'api/services';
import { Button } from 'components/Button/RoundButton';
import { Container, Wrapper } from 'components/Layout/Layout';
import { Header, HeadingContainer } from 'components/PageTitleHeader/styles';
import GDPRModal from 'components/Modals/GDPRModal';
import FeedbackModal from 'components/Modals/TicketModals/TestFeedbackModal';
import InformationModal from 'components/Modals/informationModal';
import YesNoModal from 'components/Modals/yesNoModal';
import TestStartModal from 'components/Modals/TicketModals/TestStartModal';
import { categories } from 'constants/tests';
import { Languages } from 'constants/languages';
import {
  BodyContainer,
  InformationBox,
  CategoryContainer,
  CategoryNameAndInstructionsContainer,
  CategoryName,
  TestInformationContainer,
  Information,
  FinishedLabel,
  OldReportButtonContainer,
  ScoreContainer,
  ScoreAndButtonContainer,
  CompanyLogo,
} from './styles';
import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import { getConfig } from 'functions/getConfig';
import Dropdown from 'components/CustomizableDropdown/CustomizableDropdown';

const { TicketService } = services;

export default function CandidateTicketsPage() {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackModalInfo, setFeedbackModalInfo] = useState({});

  const [showYesNoModal, setShowYesNoModal] = useState(false);
  const [yesNoModalInfo, setYesNoModalInfo] = useState({});

  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [instructionsModalText, setInstructionsModalText] = useState('');

  const [showTestStartModal, setShowTestStartModal] = useState(false);
  const [testStartObject, setTestStartObject] = useState({});

  const [availableTicketsWithCategory, setAvailableTicketsWithCategory] = useState({});
  const [isDemoTicket, setIsDemoTicket] = useState(false);
  const [candidateChoosesObject, setCandidateChoosesObject] = useState({});
  const [isAnonymizedCandidate, setIsAnonymizedCandidate] = useState(false);
  const [isArchivedLink, setIsArchivedLink] = useState(false);
  const [isExpiredLink, setIsExpiredLink] = useState(false);
  const [isAllTicketsFinished, setIsAllTicketsFinished] = useState(false);
  const [ticketCreatedDate, setTicketCreatedDate] = useState(false);
  const [showResultToCandidate, setShowResultToCandidate] = useState(false);
  const [companyId, setCompanyId] = useState(undefined);
  const [companyName, setCompanyName] = useState(undefined);
  const navigate = useNavigate();

  const {
    loadTickets,
    getCandidateCompanyConsent,
    getCompanyGDPRAgreement,
    setGDPRConsent,
    updateCandidateTicketLocation,
    updateCandidateChoosesTest,
    updateSendOldReport,
    createCandidateFeedback,
    resetState,
  } = useActions(actions);

  let {
    gdprAgreement,
    gdprAgreementConsent,
    categoryWiseTickets,
    ticketWithLocation,
    updatedChosenTestTicket,
    updatedSendOlReportTicket,
    feedback,
    companyLogo,
  } = useSelector((state) => state.CandidateTicketsPageReducer);

  const { user, token, isTicketAuth } = useSelector((state) => state.auth);

  const updateFeedbackSubmitted = useCallback(() => {
    if (!feedbackModalInfo?.category || !feedbackModalInfo?.ticket) return;
    let existingTickets = JSON.parse(JSON.stringify(availableTicketsWithCategory));
    let index = _.findIndex(existingTickets[feedbackModalInfo.category].tickets, {
      _id: feedbackModalInfo.ticket,
    });
    existingTickets[feedbackModalInfo.category].tickets[index].feedbackSubmitted = true;
    setAvailableTicketsWithCategory(existingTickets);
    setFeedbackModalInfo({});
  }, [availableTicketsWithCategory, feedbackModalInfo.category, feedbackModalInfo.ticket]);

  const checkShowResultToCandidate = useCallback(
    (ticket) => {
      return !ticket.processing && ticket.submitted && ticket.result > -1 && ticket.showResult && isAllTicketsFinished;
    },
    [isAllTicketsFinished]
  );

  useEffect(() => {
    if (user?._id) loadTickets();
  }, [user, loadTickets]);

  useEffect(() => {
    document.cookie = `isTicketUser=true; Domain=${getConfig().COOKIE}; Max-Age=5184000; Path=/; SameSite=Lax`;
    setAvailableTicketsWithCategory(categoryWiseTickets);
  }, [categoryWiseTickets]);

  useEffect(() => {
    if (!gdprAgreement && !gdprAgreementConsent && companyId) getCompanyGDPRAgreement(companyId);
  }, [gdprAgreement, gdprAgreementConsent, companyId, getCompanyGDPRAgreement]);

  useEffect(() => {
    if (ticketWithLocation._id) {
      resetState();
      if (testStartObject.category === categories.CODE.category) {
        navigate(`/freecode-test/${ticketWithLocation._id}`);
      } else {
        navigate(`/multichoice/${ticketWithLocation._id}`);
      }
    }
  }, [ticketWithLocation, navigate, resetState, testStartObject.category]);

  useEffect(() => {
    if (updatedChosenTestTicket._id) setCandidateChoosesObject({});
  }, [updatedChosenTestTicket]);

  useEffect(() => {
    if (feedback._id) updateFeedbackSubmitted();
  }, [feedback, updateFeedbackSubmitted]);

  useEffect(() => {
    const checkFinishedTickets = () => {
      let finished = 0;
      let total = 0;
      if (!_.size(availableTicketsWithCategory)) return false;
      for (const category in availableTicketsWithCategory) {
        for (const ticket of availableTicketsWithCategory[category].tickets) {
          if (ticket.archived) continue;
          if (!isTicketAuth || ticket.session === token) total++;
          if (ticket.completedAt && !ticket.processing && ticket.session === token) finished++;
        }
      }
      setIsAllTicketsFinished(total === finished);
    };
    const checkPropertiesInTickets = () => {
      let demo = false;
      let isAnonymizedCandidateTicket = false;
      let isExpiredLinkTicket = false;
      let isArchivedLinkTicket = false;
      let showResult = false;
      for (const category in availableTicketsWithCategory) {
        if (!availableTicketsWithCategory[category].isAllTicketsArchived) {
          let isAllTicketsArchived = _.every(
            _.pluck(availableTicketsWithCategory[category].tickets, 'archived'),
            (t) => t
          );
          if (isAllTicketsArchived) {
            let existingTickets = JSON.parse(JSON.stringify(availableTicketsWithCategory));
            existingTickets[category].isAllTicketsArchived = true;
            setAvailableTicketsWithCategory(existingTickets);
          }
        }
        for (const ticket of availableTicketsWithCategory[category].tickets) {
          if (ticket.showResult) showResult = true;
          if (ticket.demo && !ticket.internalTicket && ticket.session === token) demo = true;
          if (ticket.isAnonymizedCandidate) isAnonymizedCandidateTicket = true;
          if (ticket.expired && ticket.session === token) {
            isExpiredLinkTicket = true;
            setTicketCreatedDate(moment(ticket.createdAt).format('YYYY-MM-DD'));
          }
          if (ticket.archived && !ticket.expired && ticket.session === token) isArchivedLinkTicket = true;
        }
      }

      const checkFeedback = () => {
        for (const category in availableTicketsWithCategory) {
          availableTicketsWithCategory[category].tickets.some((ticket) => {
            if (localStorage.getItem(`feedbackModal${ticket._id}`)) return false;
            if ((ticket.processing || ticket.submitted) && !ticket.feedbackSubmitted && ticket.session === token) {
              setFeedbackModalInfo({
                ticket: ticket._id,
                test: ticket.test,
                testName: ticket.testName,
                company: ticket.company,
                category: category,
              });
              setShowFeedbackModal(true);
              return true;
            }
            return false;
          });
        }
      };
      checkFeedback();
      checkFinishedTickets();
      setIsDemoTicket(demo);
      setIsAnonymizedCandidate(isAnonymizedCandidateTicket);
      setIsArchivedLink(isArchivedLinkTicket);
      setIsExpiredLink(isExpiredLinkTicket);
      setShowResultToCandidate(showResult);
    };
    if (availableTicketsWithCategory) {
      if (updatedSendOlReportTicket) checkShowResultToCandidate(updatedSendOlReportTicket);
      checkPropertiesInTickets();
      let categories = _.keys(availableTicketsWithCategory);
      if (categories?.length && !companyId) {
        setCompanyId(availableTicketsWithCategory[categories[0]].tickets[0].company);
        setCompanyName(availableTicketsWithCategory[categories[0]].tickets[0].companyName);
      }
    }
  }, [
    availableTicketsWithCategory,
    companyId,
    token,
    isTicketAuth,
    checkShowResultToCandidate,
    updatedSendOlReportTicket,
  ]);

  useEffect(() => {
    if (companyId && gdprAgreementConsent === undefined) {
      getCandidateCompanyConsent(companyId);
    }
  }, [companyId, gdprAgreementConsent, getCandidateCompanyConsent]);

  const setCandidateChoosesTest = (data, ticketId) => {
    if (!data.value) return;
    setCandidateChoosesObject({ ticket: ticketId, test: data.value, testName: data.label });
    setShowYesNoModal(true);
    setYesNoModalInfo({
      title: 'Confirmation',
      body: `Are you sure you want to select ${data.label}?`,
      isCandidateChoosesConfirmation: true,
    });
  };

  const showCandidateChoosesTestDropdown = (ticket, category) => {
    let tests = JSON.parse(JSON.stringify(availableTicketsWithCategory[category].testOptions));
    tests.unshift({ label: 'Choose Test', value: '' });
    return (
      <Dropdown
        justifyContent="end"
        options={tests}
        onChange={(value) => setCandidateChoosesTest(value, ticket._id)}
        width="190px"
      />
    );
  };

  const confirmTest = (value) => {
    setShowYesNoModal(false);
    if (!value) return;
    if (yesNoModalInfo.isSendOldTestConfirmation)
      updateSendOldReport(yesNoModalInfo.ticket._id, yesNoModalInfo.ticket.oldTicketId);
    else if (yesNoModalInfo.isCandidateChoosesConfirmation)
      updateCandidateChoosesTest(candidateChoosesObject.ticket, candidateChoosesObject.test);
    setYesNoModalInfo({});
  };

  const updateGDPR = (value) => {
    setGDPRConsent(companyId, value ? { conditionalRetention: value } : {});
  };

  const setFeedback = (value, isSubmitted) => {
    setShowFeedbackModal(false);
    if (!isSubmitted) {
      localStorage.setItem(`feedbackModal${feedbackModalInfo.ticket}`, true);
      return updateFeedbackSubmitted();
    }
    let params = {
      feedback: value,
      company: feedbackModalInfo.company,
      ticket: feedbackModalInfo.ticket,
      test: [feedbackModalInfo.test],
    };
    createCandidateFeedback(params);
  };

  const getClientInfo = async () => {
    let data = {};
    try {
      let response = await fetch('https://ipinfo.io?token=cdcc73d70713a3', {
        method: 'GET',
      });
      data = response ? await response.json() : {};
    } catch (e) {
      data = {};
    }
    return {
      ip: data?.ip,
      city: data?.city,
      country: data?.country,
      userAgent: window.navigator?.userAgent,
      screen: `${window.screen.width}x${window.screen.height}`,
    };
  };

  const startTest = async (value, isInvitedToSpectate) => {
    setShowTestStartModal(false);
    if (!value) return;
    let data = await getClientInfo();
    updateCandidateTicketLocation(testStartObject.ticket, {
      ...data,
      isInvitedToSpectate: isInvitedToSpectate,
    });
  };

  const getTextForBanner = () => {
    if (isExpiredLink)
      return `this test link has been archived since it was created a long time ago ${ticketCreatedDate}. Please contact the author to get more information.`;
    if (isArchivedLink)
      return `this test link has been archived by ${
        companyName ? companyName : 'its author'
      }. Please contact the author to get more information`;
    if (isAllTicketsFinished)
      return `thank you for taking the test(s). Your
      test results have now been submitted.`;
    return `these are the tests ${
      companyName ? companyName : 'we'
    } want you to do. First read the instructions and then click on a test to start.`;
  };

  const displaySendOldReportButtons = (ticket, category) => {
    return (
      <OldReportButtonContainer>
        <Button
          small
          padding="0px 15px"
          primaryBlue
          margin="0 5px 0 0"
          primaryGreenBorder
          onClick={() => {
            setShowYesNoModal(true);
            setYesNoModalInfo({
              title: 'Confirmation',
              body: `Are you sure you want to send your old result?`,
              isSendOldTestConfirmation: true,
              ticket: ticket,
            });
          }}
        >
          Yes
        </Button>
        <Button
          small
          padding="0px 15px"
          primaryBlue
          margin="0"
          primaryGreenBorder
          onClick={() => {
            let existingTickets = JSON.parse(JSON.stringify(availableTicketsWithCategory));
            let index = _.findIndex(existingTickets[category].tickets, {
              _id: ticket._id,
            });
            existingTickets[category].tickets[index].oldTicketId = false;
            setAvailableTicketsWithCategory(existingTickets);
          }}
        >
          No
        </Button>
      </OldReportButtonContainer>
    );
  };

  const getButtonTextAndIcon = (ticket) => {
    if (ticket.processing) return ['Processing', 'fa fa-spinner fa-spin'];
    if (ticket.completedAt) return ['Finished', 'fa fa-check'];
    if (ticket.deadlineAt && new Date(ticket.extendedDeadline || ticket.deadlineAt) < new Date())
      return ['Processing', 'fa fa-spinner fa-spin'];
    if (ticket.startedAt) return ['In Progress', 'fa fa-reply'];
    return ['Start', 'fa fa-chevron-right'];
  };

  const handleButtonClick = (text, ticket, category) => {
    let instructions = availableTicketsWithCategory[category].instructions;
    if (ticket.hasFreeTextQuestions) {
      const matchPhrase = 'This is a multi-choice test, meaning you select the correct answer from a few alternatives.';
      const freeTextInstruction =
        'This test also contains a minimum of one free text question where you will type your answer.';
      instructions = instructions.replace(matchPhrase, `${matchPhrase} ${freeTextInstruction}`);
    }

    if (text === 'Start') {
      setTestStartObject({
        instructions: instructions,
        ticket: ticket._id,
        category: category,
      });
      setShowTestStartModal(true);
    } else if (text === 'In Progress') {
      setTimeout(async () => {
        // Update location in set timeout since we do not want to actually wait for it
        try {
          const data = await getClientInfo();
          await TicketService.updateCandidateTicketLocation(ticket._id, data);
        } catch (_ignore) {}
      }, 0);
      resetState();
      if (category === categories.CODE.category) {
        navigate(`/freecode-test/${ticket._id}`);
      } else {
        navigate(`/multichoice/${ticket._id}`);
      }
    }
  };

  const getLanguagesAllowed = (ticket) => ticket?.languagesAllowed?.map((t) => `${Languages[t].SimpleName}`).join(', ');

  const renderTest = (category) => {
    return availableTicketsWithCategory[category].tickets.map((ticket, index) => {
      if (ticket.archived) return null;
      let isRepeatedTicket = false;
      if (ticket.oldTicketId && !ticket.startedAt) isRepeatedTicket = true;
      let [text, icon] = getButtonTextAndIcon(ticket);
      return (
        <InformationBox key={index}>
          <Tooltip id={`languagesAllowed${ticket._id}`} place="top" className="custom-tooltip" />
          <Tooltip id={`score${ticket._id}`} place="top" className="custom-tooltip" />
          <TestInformationContainer isRepeatedTicket={isRepeatedTicket}>
            <Information>
              {ticket.testName}
              {ticket?.languagesAllowed?.length && (
                <Information>
                  Allowed languages({ticket.languagesAllowed.length})
                  <i
                    className="fa fa-info-circle ml-1"
                    aria-hidden="true"
                    data-tooltip-id={`languagesAllowed${ticket._id}`}
                    data-tooltip-content={getLanguagesAllowed(ticket)}
                    data-tooltip-variant="info"
                  />
                </Information>
              )}
              {category === categories.CODE.category && ticket.difficulty && (
                <Information>Difficulty: {ticket.difficulty}</Information>
              )}
            </Information>
            {isRepeatedTicket ? (
              <Information>Would you like to send the old test result?</Information>
            ) : (
              <>
                <Information>{ticket.timeLimit || ticket.duration} mins</Information>
                <Information>{moment(ticket.createdAt).format('YYYY-MM-DD HH:mm')}</Information>
              </>
            )}
            <Information textAlign="right">
              {ticket.isCandidateChoosesEnabled ? (
                showCandidateChoosesTestDropdown(ticket, category)
              ) : isRepeatedTicket ? (
                displaySendOldReportButtons(ticket, category)
              ) : (
                <ScoreAndButtonContainer>
                  {checkShowResultToCandidate(ticket) && (
                    <ScoreContainer
                      data-tooltip-id={`${ticket.distributionPercentage && `score${ticket._id}`}`}
                      data-tooltip-content={`The score is better than ${ticket.distributionPercentage}% of all the reported results`}
                      data-tooltip-variant="info"
                    >
                      Score:{' '}
                      {ticket.distributionPercentage
                        ? `${ticket.distributionPercentage}%`
                        : `${ticket.result}/${ticket.scale}`}
                    </ScoreContainer>
                  )}
                  <Button
                    small
                    padding="0px 15px"
                    margin="0 0 0 10px"
                    primaryBlue
                    primaryGreenBorder
                    disabled={text === 'Finished' || text === 'Processing' || !gdprAgreementConsent}
                    onClick={() => handleButtonClick(text, ticket, category)}
                  >
                    {text} <i className={icon}></i>
                  </Button>
                </ScoreAndButtonContainer>
              )}
            </Information>
          </TestInformationContainer>
        </InformationBox>
      );
    });
  };

  const isProcessingTicket = () => {
    if (
      !availableTicketsWithCategory[categories.CODE.category] ||
      !availableTicketsWithCategory[categories.CODE.category].tickets.length
    )
      return false;
    let isProcessing = false;
    for (const ticket of availableTicketsWithCategory[categories.CODE.category].tickets) {
      if (ticket.processing && !ticket.archived) isProcessing = true;
    }
    return isProcessing;
  };

  const renderCategoryWiseTestSection = () => {
    if (!_.size(availableTicketsWithCategory)) return;
    let categoryNames = _.keys(availableTicketsWithCategory);

    return categoryNames.map((category, index) => {
      if (availableTicketsWithCategory[category].isAllTicketsArchived) return null;
      return (
        <CategoryContainer key={index}>
          <CategoryNameAndInstructionsContainer>
            <CategoryName>
              {category === categories.CODE.category
                ? 'Freecode'
                : category === categories.GENERAL_SKILLS.category
                  ? 'General'
                  : 'Multichoice'}{' '}
              Tests
            </CategoryName>
            <Button
              small
              padding="5px 15px"
              margin="0 0 0 15px"
              primaryBlue
              primaryGreenBorder
              onClick={() => {
                setInstructionsModalText(availableTicketsWithCategory[category].instructions);
                setShowInstructionsModal(true);
              }}
            >
              Instructions
            </Button>
          </CategoryNameAndInstructionsContainer>
          <br />
          {category === categories.CODE.category && isProcessingTicket() && (
            <InformationBox warning margin="0 0 20px 0">
              Processing might take a while. We are running all test cases, including larger ones.
            </InformationBox>
          )}
          {renderTest(category)}
        </CategoryContainer>
      );
    });
  };

  return (
    <Wrapper padding="55px 0 0 0" minHeight="100%">
      {!gdprAgreementConsent && gdprAgreement && (
        <GDPRModal onConfirm={(value) => updateGDPR(value)} gdprAgreement={gdprAgreement} />
      )}
      {showFeedbackModal && (
        <FeedbackModal
          test={feedbackModalInfo.testName}
          confirm={(value, isSubmitted) => setFeedback(value, isSubmitted)}
        ></FeedbackModal>
      )}
      {showInstructionsModal && (
        <InformationModal
          title="Instructions"
          textHTML={instructionsModalText}
          closeModal={() => setShowInstructionsModal(false)}
        />
      )}
      {showTestStartModal && (
        <TestStartModal
          isMobile={isMobile}
          isCodeTest={testStartObject.category === categories.CODE.category}
          confirm={(value, isInvitedToSpectate) => startTest(value, isInvitedToSpectate)}
        >
          {testStartObject.instructions}
        </TestStartModal>
      )}
      {showYesNoModal && <YesNoModal modalInfo={yesNoModalInfo} confirm={(isConfirmed) => confirmTest(isConfirmed)} />}
      <Container column>
        <HeadingContainer flexOnSmallScreen>
          <Header>Tests</Header>
          {companyLogo && <CompanyLogo src={companyLogo}></CompanyLogo>}
        </HeadingContainer>
        <BodyContainer>
          {isDemoTicket && (
            <InformationBox warning textCenter margin="0 0 30px 0">
              Demo data - Limited test sent from a demo account
            </InformationBox>
          )}
          <InformationBox danger={isExpiredLink || isArchivedLink}>
            Hi <b>{isAnonymizedCandidate ? 'Candidate' : user?.name || 'Candidate'}</b>, {getTextForBanner()}
          </InformationBox>
          {renderCategoryWiseTestSection()}
          {!isExpiredLink && showResultToCandidate && !isAllTicketsFinished && (
            <FinishedLabel>Results will be shown when all tests are finished</FinishedLabel>
          )}
        </BodyContainer>
      </Container>
    </Wrapper>
  );
}
